import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, LinearProgress } from "@material-ui/core";

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";

import AppUtil from "../../../../utilities/AppUtil";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import AppTable from "../../../Admin/Component/Table/AppTable";
import TablePaginationActions from "../../../Admin/Component/Table/TablePaginationActions";
import { fetchPayrollBooking, exportPayrollBooking } from "store/slices/booking";
import moment from "moment-timezone";
import { useParams } from "react-router-dom";
import { saveAs } from 'file-saver';
import AppConfig from "config/AppConfig";
import VisibilityIcon from '@material-ui/icons/Visibility';
import AppDialog from 'components/Dialog/AppDialog';
import SegmentPayrollTable from "./SegmentPayrollTable";

const headCells = [
  {
    id: "date",
    numeric: "left",
    disablePadding: false,
    label: "label.date",
    sort: true,
  },
  {
    id: "job",
    numeric: "left",
    disablePadding: false,
    label: "label.job",
    sort: true,
  },
  {
    id: "shift",
    numeric: "left",
    disablePadding: false,
    label: "label.shift",
    sort: true,
  },
  {
    id: "workingTime",
    numeric: "left",
    disablePadding: false,
    label: "label.workingTime",
    sort: true,
  },
  {
    id: "total",
    numeric: "left",
    disablePadding: false,
    label: "title.totalHours",
    sort: true,
  },
  {
    id: "price",
    numeric: "left",
    disablePadding: false,
    label: "label.price",
    sort: true,
  },
  {
    id: "detail",
    numeric: "center",
    disablePadding: false,
    label: "label.detail",
    sort: false,
  }
];

const PayrollBookingTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [queries, setQueries] = useState({
    sort: true,
    page_number: 1,
    page_size: 10,
    search_key: "",
    sort_field: "name",
  });
  // const userList = useSelector(getUserCoordinatorList);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [showSegmentDetail, setShowSegmentDetail] = useState(false);
  const [segmentList, setSegmentList] = useState([]);

  const emptyRows = data.length === 0;

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page_number: newPage + 1 });
    AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({
      ...queries,
      page_number: 1,
      page_size: parseInt(event.target.value),
    });
    AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sort_field: property,
      sort:
        property === queries.sort_field ? queries.sort !== true : true,
    });
  };

  const _loadData = async ({ page_number, page_size, search_key, sort, sort_field } = {}) => {
    const newQueries = {
      ...queries,
      payroll_id: id
    };
    if (page_number) {
      newQueries.page_number = page_number;
    }
    if (page_size) {
      newQueries.page_size = page_size;
    }
    if (sort_field) {
      newQueries.sort_field = sort_field;
    }
    newQueries.search_key = search_key || "";
    newQueries.sort = !(!sort || false);
    setLoading(true);
    dispatch(fetchPayrollBooking(newQueries))
      .then(({ payload }) => {
        setQueries(newQueries);
        setLoading(false);
        if (payload) {
          setData(payload.content);
          setTotalElements(payload.totalElements);
        }
      });
  };

  useEffect(() => {
    _loadData();
  }, []);

  const handleExportCSV = async () => {
    setSubmitting(true);
    const result = await dispatch(exportPayrollBooking({id: id}));
    setSubmitting(false);
    if(result.payload) {
      const blob = new Blob(['\ufeff', result.payload], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `payload-booking-${moment().format('YYYYMMDD')}.csv`);
    }
  }

  const handleViewSegmentDetail = detail => {
    const { bookingRateResponse } = detail;
    let arr = [];
    // const bookingRateResponse = {
    //     shiftType: "AFTERNOON",
    //     totalHours: 5.8,
    //     totalPrice: 145,
    //     bookingSegmentRateResponses: [
    //         {
    //             id: 20,
    //             shiftType: "NIGHT",
    //             totalHours: 1,
    //             totalPrice: 10
    //         }
    //     ]
    // };
    arr.push({
        shiftType: bookingRateResponse.shiftType,
        totalHours: bookingRateResponse.totalHours,
        totalPrice: bookingRateResponse.totalPrice
    });
    arr = [...arr, ...bookingRateResponse.bookingSegmentRateResponses];
    setSegmentList(arr);
    setShowSegmentDetail(true);
}

  return (
    <Box>
      <Box className="text-right mb-3">
        <AppButton
          label={t('button.exportCSVFile')}
          onClick={() => handleExportCSV()}
          isLoading={submitting}
          buttonColor={AppColor.turquoise}
        />
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.sort === true ? "asc" : "desc"}
        orderBy={queries.sort_field}
        rowCount={data.length}
        headCells={headCells}
      >
        <TableBody>
          {loading && (<TableRow>
            <TableCell colSpan={7} padding="none">
              <LinearProgress />
            </TableCell>
          </TableRow>)}
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{moment(row.date).format(AppConfig.DATE_FORMAT.DEFAULT)}</TableCell>
              <TableCell>{row.titleJob}</TableCell>
              <TableCell>{t(`shift.${row.shiftType}`)}</TableCell>
              <TableCell>{`${moment(row.startTime).format('HH:mm')} - ${moment(row.endTime).format('HH:mm')}`}</TableCell>
              <TableCell>{row.totalHours.toFixed(2)}</TableCell>
              <TableCell>{`$${AppUtil.formatMoney(row.totalPrice.toFixed(2))}`}</TableCell>
              <TableCell>{row.bookingRateResponse ? <VisibilityIcon style={{ cursor: 'pointer' }}  onClick={() => handleViewSegmentDetail(row)}/>  : ''}</TableCell>

            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length + 1} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.page_size}
              page={queries.page_number - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count:
                    count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      <AppDialog
            title={t("title.configurationDetail")}
            maxWidth={'sm'}
            open={showSegmentDetail}
            onClose={() => {
                setShowSegmentDetail(false);
                setSegmentList([]);
            }}
            >
                <SegmentPayrollTable
                    data={segmentList}
                />
            </AppDialog>
    </Box>
  );
};

export default PayrollBookingTable;
